/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material'

interface ICaptchaModalProps {
  open: boolean
  onToken: (token: string) => void
  onClose: () => void
  onError?: (message: string) => void
}

const PROD_CAPTCHA_URL =
  'https://cardchecker.cscsonline.uk.com/smarttech/v1/get-captcha'
const CAPTCHA_URL =
  process.env.REACT_APP_ENV === 'production'
    ? PROD_CAPTCHA_URL
    : 'https://cardcheckeruat.cscsonline.uk.com/smarttech/v1/get-captcha'

// Token arrives from the backend's captcha page via window.postMessage.
// Contract:
//   event.origin === REACT_APP_API_BASE_URL origin
//   event.data === { type: 'CAPTCHA_TOKEN', captchaToken: string }

const MESSAGE_TYPE_TOKEN = 'CAPTCHA_TOKEN'
const MESSAGE_TYPE_ERROR = 'CAPTCHA_ERROR'

interface ICaptchaMessage {
  type?: string
  captchaToken?: string
  message?: string
}

const CaptchaModal: React.FC<ICaptchaModalProps> = ({
  open,
  onToken,
  onClose,
  onError,
}) => {
  const handledRef = useRef(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)

  useEffect(() => {
    if (!open) {
      handledRef.current = false
      setIframeLoaded(false)
      return
    }

    // The token arrives from the captcha page's origin, so validate against
    // the captcha URL itself rather than the API base.
    const expectedOrigin = (() => {
      try {
        return new URL(CAPTCHA_URL).origin
      } catch {
        return ''
      }
    })()

    const handleMessage = (event: MessageEvent) => {
      if (expectedOrigin && event.origin !== expectedOrigin) return

      const data = event.data as ICaptchaMessage | null
      if (!data || typeof data !== 'object') return

      if (
        data.type === MESSAGE_TYPE_TOKEN &&
        data.captchaToken &&
        !handledRef.current
      ) {
        handledRef.current = true
        onToken(data.captchaToken)
        return
      }

      if (data.type === MESSAGE_TYPE_ERROR) {
        onError?.(
          data.message || 'CAPTCHA verification failed. Please try again.',
        )
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [open, onToken, onError])

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-labelledby="captcha-modal-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 30, 50, 0.55)',
            backdropFilter: 'blur(6px)',
          },
        },
      }}
    >
      <Fade in={open} timeout={260}>
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: '100dvh', p: 4, outline: 'none' }}
        >
          <Box
            sx={{
              width: 'min(94vw, 460px)',
              borderRadius: 4,
              overflow: 'hidden',
              backgroundColor: 'bw.white',
              boxShadow:
                '0 20px 60px rgba(0, 47, 78, 0.25), 0 2px 8px rgba(0, 47, 78, 0.12)',
              position: 'relative',
            }}
          >
            {/* Header band */}
            <Box
              sx={{
                background:
                  'linear-gradient(135deg, #007AC2 0%, #57A7D7 100%)',
                color: 'bw.white',
                px: 10,
                pt: 10,
                pb: 8,
                position: 'relative',
              }}
            >
              <IconButton
                aria-label="Close"
                onClick={onClose}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.18)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Stack direction="row" spacing={4} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.22)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    id="captcha-modal-title"
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    Security check
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: 'rgba(255, 255, 255, 0.85)',
                    }}
                  >
                    Quick verification to continue scanning
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Body with iframe */}
            <Box
              sx={{
                p: 8,
                backgroundColor: 'bw.backgroundColor',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 460,
                  backgroundColor: 'bw.white',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'bw.grey',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
                }}
              >
                {!iframeLoaded && (
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    spacing={4}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'bw.white',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography
                      variant="body2"
                      sx={{ color: 'bw.greyLightest2' }}
                    >
                      Loading verification…
                    </Typography>
                  </Stack>
                )}
                <iframe
                  src={CAPTCHA_URL}
                  title="CAPTCHA challenge"
                  onLoad={() => {
                    setIframeLoaded(true)
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block',
                  }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 5,
                  textAlign: 'center',
                  color: 'bw.greyLightest2',
                }}
              >
                Protected by reCAPTCHA. Your verification token stays valid for
                subsequent checks.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Fade>
    </Modal>
  )
}

export default CaptchaModal
